import { useState } from "react";
import * as XLSX from "xlsx";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import type { CustomerCsvImportPreview } from "../lib/customers.js";

const MAX_BYTES = 5 * 1024 * 1024;

/** Reads .csv as text or converts .xlsx / .xls first sheet to CSV. */
async function readFileAsCsv(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || file.type === "text/csv") {
    return await file.text();
  }
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) throw new Error("Workbook has no sheets.");
    const sheet = wb.Sheets[sheetName];
    return XLSX.utils.sheet_to_csv(sheet);
  }
  // Fall back to text — covers .tsv-as-csv, etc. The hub parser is permissive
  // on whitespace.
  return await file.text();
}

interface Props {
  onClose: () => void;
  onApplied: () => void;
}

/**
 * ADR-0075 §3.5 — CSV import modal.
 *
 * On file selection: POST /api/tenant/admin/customers/import/preview with
 * the file text wrapped in `{ csv }`. Render preview + error list +
 * ignored-column chips. Import button POSTs to /import on confirmation.
 *
 * Server-side spec called for multipart/form-data; the hub uses a JSON
 * `{ csv: string }` body instead (see hub index.ts comment) to avoid a
 * new dependency. Console reads the file via FileReader.text().
 */
export function CustomerCsvImportModal({ onClose, onApplied }: Props) {
  const toast = useToast();
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] = useState<CustomerCsvImportPreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onFile(file: File) {
    setPreview(null);
    setPreviewErr(null);
    if (file.size > MAX_BYTES) {
      setPreviewErr("File exceeds the 5 MiB limit.");
      return;
    }
    setBusy(true);
    try {
      // Yield to the browser so the spinner paints before a heavy .xlsx parse
      // hogs the main thread.
      await new Promise((r) => setTimeout(r, 0));
      const text = await readFileAsCsv(file);
      setCsvText(text);
      const res = await apiFetch<CustomerCsvImportPreview>(
        "/api/tenant/admin/customers/import/preview",
        { method: "POST", body: { csv: text } }
      );
      setPreview(res);
    } catch (e) {
      setPreviewErr(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function applyImport() {
    if (!csvText) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ created: number; updated: number }>(
        "/api/tenant/admin/customers/import",
        { method: "POST", body: { csv: csvText } }
      );
      toast.push({
        kind: "success",
        message: `Imported ${res.created + res.updated} customers (${res.created} created, ${res.updated} updated).`
      });
      onApplied();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setBusy(false);
    }
  }

  const canImport = preview != null && preview.errors.length === 0 && !busy;

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable"
        role="dialog"
        aria-modal="true"
        aria-label="Import customers"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: "90vw" }}
      >
        <div className="modalScrollHeader">
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>Import customers</div>
        </div>
        <div className="modalScrollBody" style={{ paddingTop: 0, paddingBottom: 16 }}>
          <label className="field">
            <span className="label">CSV or Excel file (max 5 MiB)</span>
            <input
              className="input"
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
              }}
            />
            <div className="help muted">
              Accepts <code>.csv</code>, <code>.xlsx</code>, or <code>.xls</code>. For workbooks, the
              first sheet is read. Required columns: <code>account_code</code>,{" "}
              <code>display_name</code>. Optional: <code>parent_account_code</code>,{" "}
              <code>notes</code>. Other columns are ignored. The catchall flag is UI-only — set it
              on the customer detail page after import.
            </div>
          </label>

          {busy && (
            <div
              style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}
              aria-live="polite"
            >
              <span
                className="customerImportSpinner"
                role="img"
                aria-label="Working"
              />
              <span>Importing…</span>
            </div>
          )}

          {previewErr && (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>
              {previewErr}
            </div>
          )}

          {preview && (
            <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
              <div>
                <strong>{preview.would_create}</strong> would be created,{" "}
                <strong>{preview.would_update}</strong> would be updated,{" "}
                <strong>{preview.errors.length}</strong> error(s).
              </div>

              {preview.ignored_columns.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {preview.ignored_columns.map((c) => (
                    <span key={c} className="pill pillNeutral">Ignored: {c}</span>
                  ))}
                </div>
              )}

              {preview.errors.length > 0 && (
                <div className="card" style={{ background: "var(--surface-soft)" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Errors</div>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {preview.errors.slice(0, 50).map((err, i) => (
                      <li key={i}>
                        <span className="muted">Row {err.row}, {err.field}:</span> {err.message}
                      </li>
                    ))}
                    {preview.errors.length > 50 && (
                      <li className="muted">… and {preview.errors.length - 50} more</li>
                    )}
                  </ul>
                </div>
              )}

              {preview.preview_rows.length > 0 && (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>Row</th>
                        <th>Account Code</th>
                        <th>Display name</th>
                        <th>Parent</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview_rows.map((r) => (
                        <tr key={r.row_number}>
                          <td className="muted">{r.row_number}</td>
                          <td className="mono">{r.external_id}</td>
                          <td>{r.display_name}</td>
                          <td className="muted mono">{r.parent_external_id ?? "—"}</td>
                          <td>
                            <span className={`pill ${r.action === "create" ? "pillOk" : "pillNeutral"}`}>
                              {r.action}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
        <div className="modalScrollFooter">
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn btnPrimary"
              disabled={!canImport}
              onClick={() => void applyImport()}
            >
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
